import { Button } from "@/components/ui/button"
import { ActiveFilterIcon } from "@/global_filter/active_filter_icon"
import { classes } from "@/utils"
import { Search, X } from "lucide-react"
import { TextareaHTMLAttributes, useCallback, useEffect, useRef, useState } from "preact/compat"

/** Deduction table toolbar — global search matches any column; pasted lists split on tab, newline, or comma. */
export const DEDUCTION_TABLE_SEARCH_PLACEHOLDER =
  "Search all columns. You can search for multiple values at once by pasting Excel lists; tabs, newlines, or commas split values (matches any)"

/** Column header filter popover — same multivalue rules as the main search. */
export const DEDUCTION_COLUMN_FILTER_PLACEHOLDER =
  "Match any. You can search for multiple values at once by pasting Excel lists; tabs, newlines, or commas split values."

interface SearchInputProps {
  value: string | number
  onChange: (value: string | number) => void
  autoFocus?: boolean
  focusTrigger?: boolean // When this changes to true, focus the textarea
  debounceMs?: number
}

type Props = SearchInputProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value" | "children">

/**
 * A resilient search control that maintains typing responsiveness even when
 * parent components re-render due to filtering large datasets.
 *
 * Uses a native textarea (not a single-line input) so pasted values keep
 * row breaks — Excel column copies use \n/\t between cells; browsers strip
 * those from `<input type="text">`, which broke multi-value OR filtering.
 *
 * Key design decisions:
 * 1. Uses native textarea for maximum reliability with multiline paste
 * 2. Keeps display state (showClearButton) separate from field value to minimize re-renders
 * 3. Reads value directly from DOM ref instead of React state during typing
 * 4. Only triggers parent onChange via debounce, preventing render loops
 */
export function SearchInput({ value: externalValue, onChange, className, autoFocus, focusTrigger, debounceMs = 300, placeholder, rows = 2, ...props }: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChangeRef = useRef(onChange)

  // Track whether we're showing the clear button (separate from textarea value to reduce re-renders)
  const [showClearButton, setShowClearButton] = useState(() => String(externalValue) !== "")

  // Keep onChange ref up to date without causing re-renders
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Sync textarea element with external value changes (e.g., when filters are cleared externally)
  useEffect(() => {
    if (inputRef.current) {
      const currentValue = inputRef.current.value
      const newValue = String(externalValue)
      // Only update if the external value is different from what's in the textarea
      // This prevents overwriting user input during typing
      if (currentValue !== newValue) {
        inputRef.current.value = newValue
        setShowClearButton(newValue !== "")
      }
    }
  }, [externalValue])

  // Focus the textarea when autoFocus is true (on mount)
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Focus the textarea when focusTrigger changes to true (e.g., when dropdown opens)
  useEffect(() => {
    if (focusTrigger && inputRef.current) {
      // Schedule focus after dropdown animation completes
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus()
      }, 10)
      return () => clearTimeout(timeoutId)
    }
  }, [focusTrigger])

  // Debounced filter application - reads directly from textarea ref
  const scheduleUpdate = useCallback(() => {
    // Clear any existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Set new timeout to apply filter
    debounceRef.current = setTimeout(() => {
      if (inputRef.current) {
        onChangeRef.current(inputRef.current.value)
      }
    }, debounceMs)
  }, [debounceMs])

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleClear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ""
      inputRef.current.focus()
    }
    setShowClearButton(false)
    // Clear immediately without debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    onChangeRef.current("")
  }, [])

  // Use onInput for immediate response to all input changes
  const handleInput = useCallback(() => {
    const hasValue = inputRef.current?.value !== ""
    setShowClearButton(hasValue)
    scheduleUpdate()
  }, [scheduleUpdate])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      // Apply filter immediately on Enter (skip debounce)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (inputRef.current) {
        onChangeRef.current(inputRef.current.value)
      }
    }
  }, [])

  return (
    <div className="relative flex-1">
      <textarea
        {...props}
        ref={inputRef}
        rows={rows}
        defaultValue={String(externalValue)}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        spellcheck={false}
        autoComplete="off"
        className={classes(
          "flex w-full min-h-10 resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-blue-100 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          "max-h-40 overflow-y-auto whitespace-pre-wrap",
          "pr-16",
          className
        )}
      />
      <div className="absolute right-0 top-0 flex items-start gap-1 pt-2 pr-2 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-1">
          {showClearButton ? (
            <>
              <Button
                onClick={handleClear}
                variant="ghost"
                // @ts-ignore
                size="icon"
                className="h-8 w-8 text-slate-500 hover:text-slate-700">
                <X className="h-4 w-4" />
              </Button>
              <ActiveFilterIcon />
            </>
          ) : (
            <Search className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </div>
    </div>
  )
}
